import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Truck, Package, ScanLine, MapPin, ShieldCheck, ArrowRight, Zap, BarChart3, Mail, Phone, Building2, Send, Check } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const HERO_IMG = "https://images.unsplash.com/photo-1557974040-3bec341da09b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxzYW50aWFnbyUyMGNoaWxlJTIwc2t5bGluZXxlbnwwfHx8fDE3ODQ1MDYwMTF8MA&ixlib=rb-4.1.0&q=85";
const WAREHOUSE_IMG = "https://images.unsplash.com/photo-1623123096729-26b481292919?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHx3YXJlaG91c2UlMjB3b3JrZXIlMjBwYWNrYWdlJTIwc2Nhbm5pbmd8ZW58MHx8fHwxNzg0NTA2MDExfDA&ixlib=rb-4.1.0&q=85";
const VAN_IMG = "https://images.pexels.com/photos/4391469/pexels-photo-4391469.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  const [tn, setTn] = useState("");
  const nav = useNavigate();

  const track = (e) => {
    e.preventDefault();
    if (tn.trim()) nav(`/rastreo/${tn.trim().toUpperCase()}`);
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white bg-noise overflow-hidden">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-zinc-950/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/img/voycargo-logo.png" alt="VoyCargo Express" className="w-10 h-10 object-contain" />
            <span className="font-mono-tech text-[10px] px-1.5 py-0.5 bg-white/10 border border-white/20">
              EXPRESS
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a href="#contacto" className="hidden sm:block text-sm text-zinc-400 hover:text-yellow-300 px-3 py-2 font-mono-tech text-xs uppercase tracking-widest" data-testid="link-contacto">
              Contacto
            </a>
            <Link to="/rastreo" className="hidden sm:block text-sm text-zinc-400 hover:text-white px-3 py-2" data-testid="link-rastreo">
              Rastrear
            </Link>
            <a
              href="#contacto"
              data-testid="link-register-cta"
              className="hidden sm:inline-flex border border-yellow-300/40 text-yellow-300 hover:bg-yellow-300/10 font-bold text-xs px-4 py-2 uppercase tracking-widest transition-colors"
            >
              Ser aliado
            </a>
            <Link
              to="/login"
              data-testid="link-login"
              className="bg-yellow-300 text-black font-bold text-sm px-4 py-2 uppercase tracking-widest hover:bg-yellow-200 transition-colors"
            >
              Ingresar
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative border-b border-white/10">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/60 via-zinc-950/80 to-zinc-950" />
          <div className="absolute inset-0 bg-grid opacity-40" />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-6 py-24 md:py-32 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 border border-yellow-300/40 bg-yellow-300/10 px-3 py-1.5 mb-8"
            >
              <span className="w-1.5 h-1.5 bg-yellow-300 rounded-full pulse-dot" />
              <span className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest">
                Operando en Santiago · Chile
              </span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.7 }}
              className="font-display font-black text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.95] mb-6"
            >
              Ultima milla,<br />
              <span className="text-yellow-300">sin fricción.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-lg text-zinc-400 max-w-xl mb-10"
            >
              Sistema integral para retirar, gestionar en bodega y despachar tus paquetes.
              Panel para aliados, app mobile para repartidores y rastreo público en tiempo real.
            </motion.p>

            {/* Tracker box */}
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              onSubmit={track}
              className="flex items-stretch max-w-lg border border-white/20 bg-black/40 backdrop-blur"
            >
              <div className="flex items-center px-4 border-r border-white/10">
                <ScanLine className="w-5 h-5 text-yellow-300" />
              </div>
              <input
                data-testid="hero-tracking-input"
                value={tn}
                onChange={(e) => setTn(e.target.value)}
                placeholder="VOY-20260201-XXXXXX"
                className="flex-1 bg-transparent px-4 py-4 outline-none font-mono-tech text-sm"
              />
              <button
                data-testid="hero-tracking-submit"
                className="bg-yellow-300 text-black font-bold px-6 uppercase tracking-widest text-sm hover:bg-yellow-200"
              >
                Rastrear
              </button>
            </motion.form>

            <div className="flex flex-wrap items-center gap-6 mt-10 text-xs font-mono-tech text-zinc-500 uppercase tracking-widest">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-yellow-300" />Trazabilidad total</span>
              <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-yellow-300" />Tiempo real</span>
              <span className="flex items-center gap-2"><BarChart3 className="w-4 h-4 text-yellow-300" />Reportes SLA</span>
            </div>
          </div>

          {/* Right col: stats */}
          <div className="md:col-span-5 grid grid-cols-2 gap-px bg-white/10 self-center">
            {[
              { k: "7", l: "Estados de pedido" },
              { k: "4", l: "Paneles integrados" },
              { k: "0", l: "Apps que instalar" },
              { k: "24/7", l: "Rastreo público" },
            ].map((s, i) => (
              <div key={i} className="bg-zinc-950 p-6 md:p-8">
                <div className="font-display font-black text-5xl md:text-6xl text-yellow-300 tracking-tighter">{s.k}</div>
                <div className="text-xs font-mono-tech uppercase tracking-widest text-zinc-500 mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-b border-white/10 py-4 overflow-hidden bg-yellow-300 text-black">
        <div className="marquee">
          <div className="marquee-track font-display font-black text-2xl uppercase tracking-tighter">
            {Array.from({ length: 6 }).map((_, i) => (
              <span key={i} className="flex items-center gap-6">
                Retiro <span className="opacity-40">/</span>
                Bodega <span className="opacity-40">/</span>
                Despacho <span className="opacity-40">/</span>
                Entrega <span className="opacity-40">/</span>
                Evidencia <span className="opacity-40">·</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* 4 PANELS */}
      <section className="border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 py-24">
          <div className="grid md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-4">
              <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest mb-4">
                / 01 · Producto
              </div>
              <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
                Un sistema.<br />Cuatro roles.
              </h2>
            </div>
            <div className="md:col-span-7 md:col-start-6 text-zinc-400 text-lg">
              Cada actor de la cadena logística tiene su interfaz optimizada. Sin apps que
              instalar. Todo desde el navegador — incluso el escaneo QR desde el celular del
              repartidor.
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-px bg-white/10">
            {[
              { icon: ShieldCheck, k: "ADMIN", h: "Panel Voycargo", d: "Vista global de pedidos, gestión de aliados y repartidores, bodega, asignación y reportes." },
              { icon: Package, k: "CLIENTE", h: "Portal Aliados", d: "Carga manual y masiva por Excel, etiquetas QR imprimibles, historial y evidencias." },
              { icon: ScanLine, k: "REPARTIDOR", h: "App móvil web", d: "Lotes de retiro, escaneo QR desde la cámara, ruta del día, entregas con foto obligatoria." },
              { icon: MapPin, k: "PÚBLICO", h: "Rastreo abierto", d: "Cualquier destinatario consulta el estado del pedido con timeline y foto de entrega." },
            ].map((p, i) => (
              <div key={i} className="bg-zinc-950 p-10 group hover:bg-zinc-900 transition-colors">
                <div className="flex items-center justify-between mb-8">
                  <p.icon className="w-8 h-8 text-yellow-300" strokeWidth={1.5} />
                  <span className="font-mono-tech text-[10px] text-zinc-600 uppercase tracking-widest">/ 0{i + 1}</span>
                </div>
                <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest mb-3">{p.k}</div>
                <h3 className="font-display font-black text-3xl tracking-tighter mb-4">{p.h}</h3>
                <p className="text-zinc-400 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WORKFLOW */}
      <section className="border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 py-24 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest mb-4">
              / 02 · Flujo
            </div>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-6">
              De la tienda,<br />a la puerta.
            </h2>
            <p className="text-zinc-400 text-lg mb-8">
              Siete estados. Cada cambio con timestamp, responsable y notificación automática.
              Rastreable de punta a punta.
            </p>
            <img src={WAREHOUSE_IMG} alt="" className="w-full h-64 object-cover border border-white/10" />
          </div>
          <div className="md:col-span-6 md:col-start-7">
            {[
              { n: "01", t: "Creado", d: "Cliente carga el pedido y se genera número + QR + etiqueta." },
              { n: "02", t: "Pendiente", d: "Esperando que el repartidor pase a buscar." },
              { n: "03", t: "Retirado", d: "Repartidor escaneó el QR con su celular. Va en su lote." },
              { n: "04", t: "En bodega", d: "Lote cerrado. Pedido recibido para clasificación." },
              { n: "05", t: "En reparto", d: "Asignado a repartidor para entrega final." },
              { n: "06", t: "Entregado", d: "Foto de evidencia obligatoria + timestamp." },
              { n: "07", t: "No entregado", d: "Motivo + destino (bodega o mañana)." },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="border-b border-white/10 py-5 flex items-start gap-6 group hover:pl-2 transition-all"
              >
                <div className="font-mono-tech font-bold text-yellow-300 text-sm w-10">{s.n}</div>
                <div className="flex-1">
                  <div className="font-display font-bold text-xl">{s.t}</div>
                  <div className="text-sm text-zinc-500 mt-1">{s.d}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-zinc-700 group-hover:text-yellow-300 mt-2 transition-colors" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* REGISTER + CONTACT */}
      <section id="contacto" className="border-b border-white/10 bg-zinc-950">
        <div className="max-w-[1600px] mx-auto px-6 py-24 grid md:grid-cols-2 gap-px bg-white/10">
          <RegisterForm />
          <ContactForm />
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-white/10 relative">
        <div className="absolute inset-0">
          <img src={VAN_IMG} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/80 to-zinc-950/60" />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-6 py-24 md:py-32 text-center">
          <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest mb-6">
            / 03 · Ingreso
          </div>
          <h2 className="font-display font-black text-5xl md:text-7xl tracking-tighter mb-8 max-w-3xl mx-auto leading-none">
            Empezá a operar<br />en menos de 5 minutos.
          </h2>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/login"
              data-testid="cta-login"
              className="bg-yellow-300 text-black font-bold px-8 py-4 uppercase tracking-widest hover:bg-yellow-200 inline-flex items-center gap-2"
            >
              Ingresar al sistema <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/rastreo"
              data-testid="cta-rastreo"
              className="border border-white/20 hover:border-yellow-300 text-white font-bold px-8 py-4 uppercase tracking-widest"
            >
              Rastrear un pedido
            </Link>
          </div>
        </div>
      </section>

      <footer className="py-10">
        <div className="max-w-[1600px] mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-zinc-500 font-mono-tech">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-yellow-300 flex items-center justify-center">
              <Truck className="w-3 h-3 text-black" strokeWidth={2.5} />
            </div>
            <span>VOYCARGO EXPRESS © 2026 · Chile</span>
          </div>
          <span>Sistema de gestión de última milla · v1.0</span>
        </div>
      </footer>
    </div>
  );
}

// ============ REGISTER FORM (public lead) ============
function RegisterForm() {
  const [form, setForm] = useState({
    razon_social: "", nombre_contacto: "", email: "", telefono: "",
    volumen_mensual: "", comuna_bodega: "",
  });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.razon_social || !form.nombre_contacto || !form.email || !form.telefono) {
      toast.error("Completa los campos obligatorios"); return;
    }
    setLoading(true);
    try {
      await api.post("/public/register-lead", form);
      setSent(true);
      toast.success("Solicitud enviada. Te contactaremos pronto.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  if (sent) {
    return (
      <div className="bg-zinc-950 p-10 md:p-12">
        <div className="border border-yellow-300/40 bg-yellow-300/5 p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-yellow-300 mx-auto mb-4 flex items-center justify-center">
            <Check className="w-6 h-6 text-black" strokeWidth={3} />
          </div>
          <div className="font-display font-black text-2xl tracking-tighter mb-2">¡Solicitud recibida!</div>
          <div className="text-zinc-400 text-sm">Nuestro equipo te contactará en las próximas 24 hrs.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-950 p-10 md:p-12" id="registro">
      <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest mb-3">/ Sé aliado</div>
      <h3 className="font-display font-black text-3xl md:text-4xl tracking-tighter mb-3">
        Empezá a operar<br />con VoyCargo
      </h3>
      <p className="text-zinc-400 text-sm mb-8">Cuéntanos de tu tienda y coordinamos onboarding.</p>
      <form onSubmit={submit} className="space-y-3" data-testid="register-form">
        <FormField icon={Building2} label="Razón social *" value={form.razon_social} onChange={change("razon_social")} placeholder="Tienda Demo SpA" testid="reg-razon" />
        <FormField label="Nombre de contacto *" value={form.nombre_contacto} onChange={change("nombre_contacto")} placeholder="Juan Pérez" testid="reg-nombre" />
        <div className="grid grid-cols-2 gap-3">
          <FormField icon={Mail} type="email" label="Email *" value={form.email} onChange={change("email")} placeholder="juan@tienda.cl" testid="reg-email" />
          <FormField icon={Phone} label="Teléfono *" value={form.telefono} onChange={change("telefono")} placeholder="+56 9 ..." testid="reg-tel" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Volumen mensual" value={form.volumen_mensual} onChange={change("volumen_mensual")} placeholder="~500 envíos" testid="reg-vol" />
          <FormField label="Comuna bodega" value={form.comuna_bodega} onChange={change("comuna_bodega")} placeholder="Providencia" testid="reg-comuna" />
        </div>
        <button type="submit" disabled={loading} data-testid="submit-register" className="w-full bg-yellow-300 text-black font-bold py-3.5 uppercase tracking-widest text-sm hover:bg-yellow-200 disabled:opacity-50 inline-flex items-center justify-center gap-2 mt-4">
          {loading ? "Enviando..." : "Solicitar acceso"} <ArrowRight className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}

// ============ CONTACT FORM (public) ============
function ContactForm() {
  const [form, setForm] = useState({ nombre: "", email: "", telefono: "", empresa: "", mensaje: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.mensaje) {
      toast.error("Completa los campos obligatorios"); return;
    }
    setLoading(true);
    try {
      await api.post("/public/contact", form);
      setSent(true);
      toast.success("Mensaje enviado.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  if (sent) {
    return (
      <div className="bg-zinc-900/50 p-10 md:p-12">
        <div className="border border-white/10 p-8 text-center">
          <div className="w-12 h-12 rounded-full border-2 border-yellow-300 mx-auto mb-4 flex items-center justify-center">
            <Send className="w-5 h-5 text-yellow-300" />
          </div>
          <div className="font-display font-black text-2xl tracking-tighter mb-2">¡Gracias!</div>
          <div className="text-zinc-400 text-sm">Te responderemos a la brevedad.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-900/50 p-10 md:p-12">
      <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest mb-3">/ Contáctanos</div>
      <h3 className="font-display font-black text-3xl md:text-4xl tracking-tighter mb-3">
        ¿Tenés preguntas?<br />Escribinos.
      </h3>
      <p className="text-zinc-400 text-sm mb-8">Ventas, soporte, alianzas — respondemos en horario hábil.</p>
      <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
        <FormField label="Nombre *" value={form.nombre} onChange={change("nombre")} placeholder="Tu nombre" testid="con-nombre" />
        <div className="grid grid-cols-2 gap-3">
          <FormField icon={Mail} type="email" label="Email *" value={form.email} onChange={change("email")} placeholder="tu@email.cl" testid="con-email" />
          <FormField icon={Phone} label="Teléfono" value={form.telefono} onChange={change("telefono")} placeholder="+56 ..." testid="con-tel" />
        </div>
        <FormField icon={Building2} label="Empresa" value={form.empresa} onChange={change("empresa")} placeholder="Opcional" testid="con-empresa" />
        <div>
          <label className="block text-[10px] font-mono-tech uppercase tracking-widest text-zinc-500 mb-1.5">Mensaje *</label>
          <textarea value={form.mensaje} onChange={change("mensaje")} rows={4} placeholder="Contanos en qué te podemos ayudar..." className="w-full bg-black/50 border border-white/10 focus:border-yellow-300 outline-none px-3 py-2.5 text-sm resize-none" data-testid="con-mensaje" />
        </div>
        <button type="submit" disabled={loading} data-testid="submit-contact" className="w-full border-2 border-yellow-300 text-yellow-300 hover:bg-yellow-300 hover:text-black font-bold py-3.5 uppercase tracking-widest text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2 mt-4 transition-colors">
          {loading ? "Enviando..." : "Enviar mensaje"} <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}

function FormField({ icon: Icon, label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <label className="block text-[10px] font-mono-tech uppercase tracking-widest text-zinc-500 mb-1.5">{label}</label>
      <div className={`flex items-center bg-black/50 border border-white/10 focus-within:border-yellow-300 ${Icon ? "" : ""}`}>
        {Icon && <div className="pl-3 pr-1 text-zinc-500"><Icon className="w-4 h-4" /></div>}
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="flex-1 bg-transparent outline-none px-3 py-2.5 text-sm"
          data-testid={testid}
        />
      </div>
    </div>
  );
}
